import { getAuthKey, logoutClearLocal } from './local-storage/user';

import { delay } from 'redux-saga/effects';
import { message } from 'antd';

export class ResponseError extends Error {
  public response: Response;
  public parsedResponse?: any; // Add this line

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response: Response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

async function checkTokenError(response: Response) {
  const processedResponse = await response.json();
  if (processedResponse?.error === 'Invalid token.') {
    setTimeout(() => {
      if (getAuthKey()) {
        logoutClearLocal();
        window.location.reload();
      }
    }, 2000);
  }
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  if (response.status === 401) checkTokenError(response);
  if (response.status === 403) {
    return response.json().then(parsedResponse => {
      const error = new ResponseError(response);
      error.response = response;
      error.parsedResponse = parsedResponse; // Now this is valid
      throw error;
    });
  }
  const error = new ResponseError(response);
  error.response = response;
  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url           The URL we want to request
 * @param  {object} [body]        The options we want to pass to "fetch"
 * @param  {boolean} needsAuth    To request with token
 * @param  {any} headers          The headers we want to pass to "fetch"
 * @param  {string} method        The method to use for the request
 * @param  {any} responseType     Response type for options
 *
 * @return {object}           The response data
 */
export async function request(
  url: string,
  body?: any,
  needsAuth: boolean = true,
  headers: any = {},
  method: string = 'POST',
  responseType?: any,
): Promise<{} | { err: ResponseError }> {
  delay(500);

  try {
    if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';

    if (needsAuth) {
      headers.Authorization = `Token ${getAuthKey()}`;
    }

    let requestUrl = url;
    if (method === 'GET' && body) {
      const queryParams = new URLSearchParams();
      Object.entries(body).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      requestUrl = queryString ? `${url}?${queryString}` : url;
    }

    const options = {
      method,
      headers,
      ...(method !== 'GET' && method !== 'HEAD'
        ? { body: JSON.stringify(body) }
        : {}),
      responseType,
    };

    const fetchResponse = await fetch(requestUrl, options);
    const response = await checkStatus(fetchResponse);
    return parseJSON(response);
  } catch (e: any) {
    if (e.parsedResponse) {
      return { message: e.parsedResponse.error, status: e.response.status };
    }
    const errMsg = e?.response?.statusText;
    if (navigator.onLine) {
      if (!errMsg) message.info('Connection issue! Please contact admin.');
    } else {
      message.warning('Internet connection unstable!');
    }
    return { message: errMsg, status: e?.response?.status };
  }
}
